import { useQuery } from '@tanstack/react-query';
import { type BookingDetail } from '@/lib/bookings';
import { paymentScheduleQuery, type SystemDivergence } from '@/lib/payments';
import { usePermissions } from '@/lib/me';
import { errorMessage } from '@/lib/errors';
import { formatMoney } from '@/lib/format';
import { financeSettingsQuery } from '@/lib/finance';
import { Card } from './cards';
import { AddScheduleEntryForm, ScheduleEntryRow } from './ScheduleEditor';

// PaymentsCard surfaces the booking's expected-payment schedule (the cashflow
// expectation layer, ADR-033): a received/outstanding summary plus per-entry
// status, with reconciled entries shown as received. Loaded lazily.
//
// The write affordances (add/edit/mark received/void — OH-124 slice 2) are
// gated on payments.schedule_write: without it, a viewer gets exactly the
// read-only card this component always rendered (I12 — the controls do not
// render, they are not merely disabled). ScheduleEntryRow renders the
// card-state line and any system_divergence hint for every viewer, since
// those are read enhancements of data the GET response already returns to
// anyone with bookings.read, not a write capability.
export function PaymentsCard({ booking }: { booking: BookingDetail }) {
  const { data, isPending, isError, error } = useQuery(paymentScheduleQuery(booking.id));
  const { can } = usePermissions();
  const canWrite = can('payments.schedule_write');
  // finance.confirm gates the "Confirm receipt" button (I12 — it does not
  // render, not merely disable, for a viewer without it) and the finance
  // settings fetch, which only that dialog needs (business_tz for its
  // default date).
  const canConfirm = can('finance.confirm');
  const canReadFinance = can('finance.read');
  const { data: settings } = useQuery({ ...financeSettingsQuery, enabled: canConfirm });
  // Ruling FR-19 (a): income_side_enabled comes with the schedule itself,
  // not from GET /finance/settings — that endpoint needs finance.read, and
  // an editor with payments.schedule_write alone would otherwise read
  // "pre-cutover" and be offered Edit/Void/Mark received on a
  // money_observed row the server then refuses (409/422).
  const incomeSideEnabled = data?.income_side_enabled ?? false;

  const divergenceByEntry = new Map<string, SystemDivergence>(
    (data?.system_divergence ?? []).map((d) => [d.entry_id, d]),
  );

  const hasEntries = !!data && data.entries.length > 0;

  return (
    <Card title="Payments">
      {isPending ? (
        <p className="text-sm text-muted">Loading…</p>
      ) : isError ? (
        <p role="alert" className="text-sm text-destructive">{errorMessage(error)}</p>
      ) : !data || (!hasEntries && !canWrite) ? (
        <p className="text-sm text-muted">No payment schedule yet.</p>
      ) : (
        <div className="space-y-3">
          {hasEntries && (
            <dl className="grid gap-3 sm:grid-cols-2">
              <div className="rounded-[var(--radius-control)] bg-[var(--surface-sunken)] p-4">
                <dt className="text-sm text-muted">Received</dt>
                <dd className="mt-1 text-2xl font-semibold tabular-nums text-foreground">
                  {formatMoney(data.received_total_minor, data.currency)}
                </dd>
              </div>
              <div className="rounded-[var(--radius-control)] bg-[var(--surface-warm)] p-4">
                <dt className="text-sm text-muted">Outstanding</dt>
                <dd className="mt-1 text-2xl font-semibold tabular-nums text-foreground">
                  {formatMoney(data.outstanding_total_minor, data.currency)}
                </dd>
              </div>
            </dl>
          )}
          {hasEntries ? (
            <ul className="divide-y divide-[var(--border-hairline)]">
              {data.entries.map((e) => (
                <ScheduleEntryRow
                  key={e.id}
                  bookingId={booking.id}
                  entry={e}
                  canWrite={canWrite}
                  canConfirm={canConfirm}
                  canReadFinance={canReadFinance}
                  incomeSideEnabled={incomeSideEnabled}
                  businessTz={settings?.business_tz}
                  divergence={divergenceByEntry.get(e.id)}
                />
              ))}
            </ul>
          ) : (
            <p className="text-sm text-muted">No payment schedule yet.</p>
          )}
          {canWrite && <AddScheduleEntryForm bookingId={booking.id} />}
        </div>
      )}
    </Card>
  );
}
